/**
 * Copyright (c) 2018-present, NDLA.
 *
 * This source code is licensed under the GPLv3 license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { gql, type TypedDocumentNode } from "@apollo/client";
import { useSuspenseQuery } from "@apollo/client/react";
import { Heading, Skeleton } from "@ndla/primitives";
import { styled } from "@ndla/styled-system/jsx";
import { type CSSProperties, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { FILM_ID } from "../../constants";
import type {
  GQLResourceTypeMoviesQuery,
  GQLResourceTypeMoviesQueryVariables,
  GQLSelectionMovieGrid_MovieFragment,
} from "../../graphqlTypes";
import { FilmContentCard } from "./FilmContentCard";
import type { MovieResourceType } from "./resourceTypes";

const StyledSection = styled("section", {
  base: {
    display: "flex",
    flexDirection: "column",
    gap: "xsmall",
  },
});

const MovieListing = styled("div", {
  base: {
    display: "grid",
    gap: "medium",
    gridTemplateColumns: "repeat(2, 1fr)",
    tablet: {
      gridTemplateColumns: "repeat(3, 1fr)",
    },
    desktop: {
      gridTemplateColumns: "repeat(4, 1fr)",
    },
  },
});

const StyledFilmContentCard = styled(FilmContentCard, {
  base: {
    animationFillMode: "backwards",
    animationName: "fade-in",
    animationDuration: "[300ms]",
    animationDelay: "[calc(var(--index) * 50ms)]",
  },
});

interface MovieGridLoadingShimmerProps {
  showHeading?: boolean;
}

export const MovieGridLoadingShimmer = ({ showHeading }: MovieGridLoadingShimmerProps) => {
  return (
    <StyledSection>
      {!!showHeading && (
        <Skeleton>
          <Heading textStyle="title.large" fontWeight="bold" asChild consumeCss>
            <h3>&nbsp;</h3>
          </Heading>
        </Skeleton>
      )}
      <MovieListing>
        {Array.from({ length: 24 }).map((_, index) => (
          <Skeleton key={index}>
            <StyledFilmContentCard
              movie={{
                __typename: "Movie",
                id: `dummy-${index}`,
                resourceTypes: [],
                url: "",
                title: "",
                metaDescription: "",
                metaImage: null,
              }}
            />
          </Skeleton>
        ))}
      </MovieListing>
    </StyledSection>
  );
};

interface Props {
  resourceType: MovieResourceType;
}

export const MovieGrid = ({ resourceType }: Props) => {
  const { t } = useTranslation();

  return (
    <StyledSection>
      <Heading textStyle="title.large" fontWeight="bold" asChild consumeCss>
        <h3>{t(resourceType.name)}</h3>
      </Heading>
      <Suspense fallback={<MovieGridLoadingShimmer />}>
        <MovieGridListing resourceType={resourceType} />
      </Suspense>
    </StyledSection>
  );
};

const MovieGridListing = ({ resourceType }: Props) => {
  const { i18n } = useTranslation();
  const resourceTypeMovies = useSuspenseQuery(resourceTypeMoviesQuery, {
    variables: {
      resourceType: resourceType.id,
      language: i18n.language,
    },
  });

  return (
    <MovieListing>
      {resourceTypeMovies.data?.searchWithoutPagination?.results?.map((movie, index) => {
        if (movie.__typename === "ArticleSearchResult" || movie.__typename === "LearningpathSearchResult") {
          const context = movie.contexts.find((c) => c.rootId === FILM_ID);
          return (
            <StyledFilmContentCard
              style={{ "--index": index } as CSSProperties}
              key={`${resourceType.id}-${index}`}
              movie={{
                ...movie,
                __typename: "Movie",
                metaImage: {
                  __typename: "MetaImage",
                  url: movie.metaImage?.url ?? "",
                  alt: "",
                },
                resourceTypes: [],
                url: context?.url ?? "",
              }}
            />
          );
        }
        return null;
      })}
    </MovieListing>
  );
};

interface SelectionMovieGridProps {
  name: string;
  movies: GQLSelectionMovieGrid_MovieFragment[];
}

export const SelectionMovieGrid = ({ name, movies }: SelectionMovieGridProps) => {
  return (
    <StyledSection>
      <Heading textStyle="title.large" fontWeight="bold" asChild consumeCss>
        <h3>{name}</h3>
      </Heading>
      <MovieListing>
        {movies.map((movie, index) => (
          <StyledFilmContentCard style={{ "--index": index } as CSSProperties} key={`${name}-${index}`} movie={movie} />
        ))}
      </MovieListing>
    </StyledSection>
  );
};

SelectionMovieGrid.fragments = {
  movie: gql`
    fragment SelectionMovieGrid_Movie on Movie {
      ...FilmContentCard_Movie
    }
    ${FilmContentCard.fragments.movie}
  `,
};

const resourceTypeMoviesQuery: TypedDocumentNode<GQLResourceTypeMoviesQuery, GQLResourceTypeMoviesQueryVariables> = gql`
  query resourceTypeMovies($resourceType: String!, $language: String!) {
    searchWithoutPagination(
      resourceTypes: $resourceType
      language: $language
      fallback: "true"
      subjects: "urn:subject:20"
      contextTypes: "standard"
      sort: "title"
    ) {
      results {
        id
        metaDescription
        ... on ArticleSearchResult {
          metaImage {
            url
          }
        }
        ... on LearningpathSearchResult {
          metaImage {
            url
          }
        }
        title
        contexts {
          contextId
          url
          rootId
        }
      }
    }
  }
`;
